import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Button,
    Input,
    VStack,
    Text,
    Box,
    Spinner,
    useToast,
    List,
    ListItem,
    Flex,
} from "@chakra-ui/react";
import React, { useState, useContext, useEffect, useRef } from "react";
import { appRequest } from "routes";
import { AppContext } from "AppProvider/AppContext";
import _ from "lodash";

interface PaymentUserModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const PaymentUserModal = ({ isOpen, onClose }: any) => {
    const { workflowProcess, toastNotify } = useContext(AppContext);
    const toast = useToast();
    const [search, setSearch] = useState("");
    const [users, setUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [selectedUser, setSelectedUser] = useState<any>(null);

    // Search users
    const handleSearch = async (value: string) => {
        if (!value) {
            setUsers([]);
            return;
        }

        try {
            setLoading(true);
            // Assuming 'user/get' supports 'value' for search
            const response: any = await appRequest("user", "get", {
                value,
                limit: 10,
                page: 1,
            });

            if (response && Array.isArray(response.data)) {
                setUsers(response.data);
            } else {
                setUsers([]);
            }
        } catch (error) {
            console.error("Error searching users:", error);
        } finally {
            setLoading(false);
        }
    };

    // Debounce search
    const debouncedSearch = useRef(_.debounce((nextValue) => handleSearch(nextValue), 500)).current;

    const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearch(value);
        setSelectedUser(null); // Clear selection on new search
        debouncedSearch(value);
    };

    const handleSelectUser = (user: any) => {
        setSelectedUser(user);
        setSearch(user.name);
        setUsers([]); // Clear suggestions
    };

    const handleSubmit = async () => {
        if (!selectedUser) {
            toastNotify({
                title: "Error",
                message: "Please select a user",
                type: "error"
            });
            return;
        }

        if (!workflowProcess?.processName || !workflowProcess?.processType) {
            toastNotify({
                title: "Error",
                message: "Workflow process not selected in context",
                type: "error"
            });
            return;
        }

        try {
            setSubmitting(true);
            const response: any = await appRequest("user", "updatePaymentUser", {
                userId: selectedUser._id || selectedUser.id,
                processName: workflowProcess.processName,
                processType: workflowProcess.processType,
            });

            if (response.status === "success") {
                toastNotify({
                    title: "Success",
                    message: "User workflow updated successfully",
                    type: "success"
                });
                onClose();
                setSearch("");
                setSelectedUser(null);
            } else {
                toastNotify({
                    title: "Error",
                    message: response.data || "Failed to update workflow",
                    type: "error"
                });
            }
        } catch (error: any) {
            toastNotify({
                title: "Error",
                message: error.message || "Something went wrong",
                type: "error"
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Add Payment User</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack spacing={4} align="stretch">
                        <Box>
                            <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={1}>
                                Review Process Context:
                            </Text>
                            <Box p={2} bg="gray.50" borderRadius="md" border="1px dashed" borderColor="gray.300">
                                <Text fontSize="xs"><b>Process Name:</b> {workflowProcess?.processName || "N/A"}</Text>
                                <Text fontSize="xs"><b>Process Type:</b> {workflowProcess?.processType || "N/A"}</Text>
                            </Box>
                        </Box>
                        <Box position="relative">
                            <Text mb={2} fontSize="sm" fontWeight="medium">
                                Search User
                            </Text>
                            <Input
                                placeholder="Type name to search..."
                                value={search}
                                onChange={onSearchChange}
                            />
                            {loading && (
                                <Box position="absolute" right={3} top="38px" zIndex={2}>
                                    <Spinner size="sm" color="teal.500" />
                                </Box>
                            )}

                            {users.length > 0 && !selectedUser && (
                                <List
                                    position="absolute"
                                    top="70px"
                                    left={0}
                                    right={0}
                                    bg="white"
                                    boxShadow="lg"
                                    borderRadius="md"
                                    zIndex={10}
                                    maxH="200px"
                                    overflowY="auto"
                                    border="1px solid"
                                    borderColor="gray.100"
                                >
                                    {users.map((user) => (
                                        <ListItem
                                            key={user._id || user.id}
                                            p={2}
                                            cursor="pointer"
                                            _hover={{ bg: "teal.50" }}
                                            onClick={() => handleSelectUser(user)}
                                            borderBottom="1px solid"
                                            borderColor="gray.50"
                                        >
                                            <Text fontWeight="medium" fontSize="sm">{user.name}</Text>
                                            <Text fontSize="xs" color="gray.500">{user.email}</Text>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Box>

                        {selectedUser && (
                            <Box p={3} bg="teal.50" borderRadius="md" border="1px solid" borderColor="teal.200">
                                <Text fontSize="sm" fontWeight="bold" color="teal.800">
                                    Selected User:
                                </Text>
                                <Text fontSize="md">{selectedUser.name}</Text>
                                <Text fontSize="xs" color="teal.600">{selectedUser.email}</Text>
                            </Box>
                        )}

                    </VStack>
                </ModalBody>

                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        colorScheme="teal"
                        onClick={handleSubmit}
                        isLoading={submitting}
                        isDisabled={!selectedUser}
                    >
                        Save User to Workflow
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default PaymentUserModal;
